import { Localized } from "@fluent/react";
import { Alert, AlertTitle, Autocomplete, Box, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, MenuItem, Paper, Skeleton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { AppId, AppTextFieldHandler } from "../../../app/appTypes";
import { updateDocument } from "../documentsApi";
import { loadDocumentFullVersionHistory, DocumentChangeLog } from "../documentHistory/documentsChangeLogApi";
import { showError, showSuccess } from "../../notifications/notificationsSlice";
import { format, parseJSON } from "date-fns";
import { SaveButton } from "../../../app/components/SaveButton";
import { CancelButton } from "../../../app/components/CancelButton";
import { LoadUsers } from "../../../app/AppDataLoader";
import { selectAllUsers, User } from "../../users/usersSlice";

export interface DocumentSysUpdateDialogProps {
    documentId: AppId;
    onSuccess: (documentId: AppId) => void;
    onCancel: () => void;
}

export const DocumentSysUpdateDialog = ({
    documentId,
    onSuccess,
    onCancel,
}: DocumentSysUpdateDialogProps) => {
    const dispatch = useAppDispatch();
    const allUsers = useAppSelector(selectAllUsers);

    const [date, setDate] = useState("");
    const [status, setStatus] = useState("");
    const [author, setAuthor] = useState<User | null>(null);
    const [authorUserNameFromDocument, setAuthorUserNameFromDocument] = useState("");
    const [opinionDate, setOpinionDate] = useState("");
    const [approvementDate, setApprovementDate] = useState("");
    const [password, setPassword] = useState("");
    const [changeLogs, setChangeLogs] = useState<DocumentChangeLog[]>([]);
    const [running, setRunning] = useState(false);
    const [serverError, setServerError] = useState("");
    const [validationErrors, setValidationErrors] = useState(false);
    const [changeCreateDate, setChangeCreateDate] = useState(false);
    const [changeStatus, setChangeStatus] = useState(false);
    const [changeAuthor, setChangeAuthor] = useState(false);
    const [changeTeamDates, setChangeTeamDates] = useState(false);
    const [changeChangeLogs, setChangeChangeLogs] = useState(false);

    const passwordError = validationErrors && !password?.trim();
    const hasAnyOperation = changeCreateDate || changeStatus || changeAuthor || changeTeamDates || changeChangeLogs;
    const operationError = validationErrors && !hasAnyOperation;
    const dateError = validationErrors && changeCreateDate && !date?.trim();
    const statusError = validationErrors && changeStatus && !status?.trim();
    const authorError = validationErrors && changeAuthor && !author;
    const opinionDateError = validationErrors && changeTeamDates && !opinionDate?.trim();
    const approvementDateError = validationErrors && changeTeamDates && !approvementDate?.trim();

    const handleUpdateDocumentClick = async () => {
        setValidationErrors(true);

        if (
            documentId &&
            password?.trim() &&
            hasAnyOperation &&
            !dateError &&
            !statusError &&
            !authorError &&
            !opinionDateError &&
            !approvementDateError
        ) {
            try {
                setRunning(true);

                const payload: any = {
                    documentId,
                    password,
                };

                if (changeCreateDate) {
                    payload.date = date;
                }

                if (changeStatus) {
                    payload.status = status;
                }

                if (changeAuthor && author) {
                    payload.author = author.userName;
                }

                if (changeTeamDates) {
                    const teamOpinionDate = new Date(opinionDate).toISOString();
                    const teamApprovementDate = new Date(approvementDate).toISOString();
                    payload.opinionDate = teamOpinionDate;
                    payload.approvementDate = teamApprovementDate;
                }

                if (changeChangeLogs) {
                    payload.changeLogs = changeLogs.map(cl => ({
                        id: cl.id,
                        createDate: cl.createDate,
                    }));
                }

                const result = await dispatch(
                    updateDocument(payload)
                );

                if (updateDocument.fulfilled.match(result)) {
                    dispatch(showSuccess("sent"));
                    onSuccess(documentId);
                    window.location.reload();
                } else {
                    let errorMsg = "error";
                    if ((result as any).payload?.kind === "http") {
                        const problem = (result as any).payload.problem;
                        if (problem?.title) errorMsg = problem.title;
                    }
                    setServerError(errorMsg);
                }
            } catch {
                dispatch(showError("error"));
            } finally {
                setRunning(false);
                setValidationErrors(false);
            }
        }
    };

    const handleDateFieldChange: AppTextFieldHandler = useCallback(
        (e) => setDate(e.target.value),
        []
    );
    const handleStatusFieldChange: AppTextFieldHandler = useCallback(
        (e) => setStatus(e.target.value),
        []
    );
    const handleOpinionDateFieldChange: AppTextFieldHandler = useCallback(
        (e) => setOpinionDate(e.target.value),
        []
    );
    const handleApprovementDateFieldChange: AppTextFieldHandler = useCallback(
        (e) => setApprovementDate(e.target.value),
        []
    );
    const handlePasswordFieldChange: AppTextFieldHandler = useCallback(
        (e) => setPassword(e.target.value),
        []
    );

    useEffect(() => {
        if (!documentId) return;
        dispatch(loadDocumentFullVersionHistory({ documentId: documentId as AppId } as any))
            .then((r: any) => {
                if (r?.payload) {
                    const versions = r.payload as any[];
                    const logs: DocumentChangeLog[] = [];
                    versions.forEach(v => {
                        if(v.id === Number(documentId)) {
                            setDate(formatForInput(v.createDate));
                            setStatus(v.status);
                            setAuthorUserNameFromDocument(v.createdByUserName ?? v.createdBy ?? "");
                        }

                        if (v.changeLogs) logs.push(...v.changeLogs);
                    })
                    setChangeLogs(logs);
                }
            })
            .catch(() => { });
    }, [dispatch, documentId]);

    useEffect(() => {
        if(!authorUserNameFromDocument || allUsers.length === 0) {
            return;
        }

        const existingAuthor = allUsers.find(u => u.userName === authorUserNameFromDocument) ?? null;
        setAuthor(existingAuthor);
    }, [allUsers, authorUserNameFromDocument]);

    const formatForInput = (iso?: string | null) => {
        if (!iso) return "";
        try {
            const d = parseJSON(iso as any);
            return format(d, "yyyy-MM-dd'T'HH:mm");
        } catch {
            return "";
        }
    }

    const handleChangeLogDateChange = (index: number, value: string) => {
        setChangeLogs(prev => {
            const copy = [...prev];
            const iso = value ? new Date(value).toISOString() as any : null;
            copy[index] = { ...copy[index], createDate: iso };
            return copy;
        })
    }

    return (
        <Dialog open maxWidth="xl" fullWidth>
            <DialogTitle
                sx={(t) => ({
                    bgcolor: t.palette.grey[100],
                    borderBottom: `1px solid ${t.palette.divider}`,
                    py: 1.5,
                    fontWeight: 700,
                })}
            >
                <Localized id="document-sys-update-dialog-title">
                    <span>_Zaawansowana edycja dokumentu</span>
                </Localized>
            </DialogTitle>

            <DialogContent dividers sx={{ pt: 2 }}>
                <Stack spacing={2}>
                    <Alert severity="warning" variant="outlined">
                        <AlertTitle>
                            <Localized id="attention">
                                <span>_Uwaga</span>
                            </Localized>
                        </AlertTitle>
                        <Localized id="document-sys-update-attention-content">
                            <span>_Używasz narzędzia omijającego standardowy obieg dokumentu. Ręczna modyfikacja dat lub statusów może wpłynąć na spójność starszych dokumentów.</span>
                        </Localized>
                    </Alert>

                    {operationError && (
                        <Alert severity="error">
                            <Localized id="document-sys-update-required">
                                <span>_Wybierz co najmniej jedną zmianę</span>
                            </Localized>
                        </Alert>
                    )}

                    <Stack spacing={1}>
                        <FormControlLabel
                            control={<Checkbox checked={changeStatus} onChange={(_, checked) => setChangeStatus(checked)} />}
                            label={<Localized id="document-sys-update-change-status">_Chcę zmienić status dokumentu</Localized>}
                        />
                        {changeStatus && (
                            <TextField
                                fullWidth
                                size="medium"
                                value={status}
                                select
                                error={statusError}
                                required
                                InputLabelProps={{ shrink: true }}
                                onChange={handleStatusFieldChange}
                                label={
                                    <Localized id="status">
                                        <span>_Status</span>
                                    </Localized>
                                }
                                helperText={
                                    statusError ? (
                                        <Localized id="required">
                                            <span>_Wymagane</span>
                                        </Localized>
                                    ) : " "
                                }
                            >
                                <MenuItem value={"N"}>
                                    <Localized id="document-status-n">Nowy</Localized>
                                </MenuItem>
                                <MenuItem value={"D"}>
                                    <Localized id="document-status-d">Zaopiniowany</Localized>
                                </MenuItem>
                                <MenuItem value={"I"}>
                                    <Localized id="document-status-i">Do zatwierdzenia</Localized>
                                </MenuItem>
                                <MenuItem value={"H"}>
                                    <Localized id="document-status-h">Zawieszony</Localized>
                                </MenuItem>
                                <MenuItem value={"F"}>
                                    <Localized id="document-status-f">Zakończony</Localized>
                                </MenuItem>
                                <MenuItem value={"C"}>
                                    <Localized id="document-status-c">Korekta</Localized>
                                </MenuItem>
                                <MenuItem value={"A"}>
                                    <Localized id="document-status-a">Zatwierdzony</Localized>
                                </MenuItem>
                                <MenuItem value={"P"}>
                                    <Localized id="document-status-p">Opublikowany</Localized>
                                </MenuItem>
                                <MenuItem value={"E"}>
                                    <Localized id="document-status-e">Zaimportowany</Localized>
                                </MenuItem>
                                <MenuItem value={"O"}>
                                    <Localized id="document-status-o">Opiniowanie</Localized>
                                </MenuItem>
                                <MenuItem value={"R"}>
                                    <Localized id="document-status-r">Odrzucony</Localized>
                                </MenuItem>
                            </TextField>
                        )}

                        <FormControlLabel
                            control={<Checkbox checked={changeCreateDate} onChange={(_, checked) => setChangeCreateDate(checked)} />}
                            label={<Localized id="document-sys-update-change-date">_Chcę zmienić datę utworzenia dokumentu</Localized>}
                        />
                        {changeCreateDate && (
                            <TextField
                                fullWidth
                                size="medium"
                                type="datetime-local"
                                value={date}
                                error={dateError}
                                required
                                InputLabelProps={{ shrink: true }}
                                onChange={handleDateFieldChange}
                                label={
                                    <Localized id="date">
                                        <span>_Data</span>
                                    </Localized>
                                }
                                helperText={
                                    dateError ? (
                                        <Localized id="required">
                                            <span>_Wymagane</span>
                                        </Localized>
                                    ) : " "
                                }
                            />
                        )}

                        <FormControlLabel
                            control={<Checkbox checked={changeAuthor} onChange={(_, checked) => setChangeAuthor(checked)} />}
                            label={<Localized id="document-sys-update-change-author">_Chcę zmienić autora dokumentu</Localized>}
                        />
                        {changeAuthor && (
                            <LoadUsers component={<Skeleton variant="text" />}>
                                <Autocomplete
                                    options={allUsers}
                                    value={author}
                                    onChange={(_, user) => setAuthor(user)}
                                    getOptionLabel={(user) => `${user.firstname} ${user.surname} (${user.email})`}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            error={authorError}
                                            helperText={authorError ? "Wymagane" : ""}
                                            label={<Localized id="document-author">_Autor</Localized>}
                                        />
                                    )}
                                />
                            </LoadUsers>
                        )}

                        <FormControlLabel
                            control={<Checkbox checked={changeTeamDates} onChange={(_, checked) => setChangeTeamDates(checked)} />}
                            label={<Localized id="document-sys-update-change-dates">_Chcę ustawić daty opiniowania i zatwierdzeń</Localized>}
                        />
                        {changeTeamDates && (
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                <TextField
                                    fullWidth
                                    size="medium"
                                    type="datetime-local"
                                    value={opinionDate}
                                    error={opinionDateError}
                                    required
                                    InputLabelProps={{ shrink: true }}
                                    onChange={handleOpinionDateFieldChange}
                                    label={<Localized id="opinion-date">_Data opiniowania</Localized>}
                                    helperText={opinionDateError ? "Wymagane" : " "}
                                />
                                <TextField
                                    fullWidth
                                    size="medium"
                                    type="datetime-local"
                                    value={approvementDate}
                                    error={approvementDateError}
                                    required
                                    InputLabelProps={{ shrink: true }}
                                    onChange={handleApprovementDateFieldChange}
                                    label={<Localized id="approvement-date">_Data zatwierdzenia</Localized>}
                                    helperText={approvementDateError ? "Wymagane" : " "}
                                />
                            </Stack>
                        )}

                        <FormControlLabel
                            control={<Checkbox checked={changeChangeLogs} onChange={(_, checked) => setChangeChangeLogs(checked)} />}
                            label={<Localized id="document-sys-update-change-changelog">_Chcę zmienić historię zmian</Localized>}
                        />
                        {changeChangeLogs && (
                            <Box sx={{ mt: 1 }}>
                                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                                    <Localized id="document-changelog-description">_Historia zmian</Localized>
                                </Typography>
                                <TableContainer component={Paper} sx={{ minHeight: 220, maxHeight: 380 }}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell><Localized id="document-changelog-change-reason">_Powód zmiany</Localized></TableCell>
                                                <TableCell><Localized id="document-changelog-change-description">_Opis zmiany</Localized></TableCell>
                                                <TableCell><Localized id="date">_Data</Localized></TableCell>
                                                <TableCell><Localized id="document-author">_Autor</Localized></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {changeLogs.map((log, idx) => (
                                                <TableRow key={log.id || idx}>
                                                    <TableCell>{log.changeReason}</TableCell>
                                                    <TableCell>{log.changeDescription}</TableCell>
                                                    <TableCell>
                                                        <TextField
                                                            type="datetime-local"
                                                            size="small"
                                                            value={formatForInput((log as any).createDate as any)}
                                                            onChange={(e) => handleChangeLogDateChange(idx, e.target.value)}
                                                            InputLabelProps={{ shrink: true }}
                                                        />
                                                    </TableCell>
                                                    <TableCell>{log.createdByFullName}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        )}
                    </Stack>
                </Stack>

                <TextField
                    fullWidth
                    size="medium"
                    margin="normal"
                    error={passwordError}
                    type="password"
                    required
                    InputLabelProps={{ shrink: true }}
                    value={password}
                    onChange={handlePasswordFieldChange}
                    label={
                        <Localized id="password">
                            <span>_Hasło</span>
                        </Localized>
                    }
                    helperText={
                        passwordError ? (
                            <Localized id="autorization-password-required">
                                <span>_Wymagana autoryzacja hasłem</span>
                            </Localized>
                        ) : " "
                    }
                    autoComplete="off"
                />

                {serverError && (
                    <Alert
                        severity="error"
                        onClose={() => setServerError("")}
                        sx={{ mt: 2 }}
                    >
                        <Localized id={serverError}>
                            <span>Server error</span>
                        </Localized>
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <SaveButton loading={running} onClick={handleUpdateDocumentClick} />
                <CancelButton loading={running} onClick={onCancel} />
            </DialogActions>
        </Dialog>
    );
};
